// client/src/hooks/useHorarios.js - VERSÃO COMPLETA CORRIGIDA
import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../services/api';
import useAlert from './useAlert';
import { parseISO, format, isSameDay } from 'date-fns';

const HORARIOS_QUERY_KEY = 'horarios';

const fetchHorarios = async (params) => {
    // Limpar propriedades vazias ou nulas
    const cleanParams = Object.fromEntries(
        Object.entries(params).filter(([_, v]) => v != null && v !== '')
    );
    
    if (!cleanParams.tipoEntidade || !cleanParams.entidadeAgendavel) {
        return [];
    }
    
    try {
        const { data } = await apiClient.get('/horarios', { params: cleanParams });
        return data.data || [];
    } catch (error) {
        console.error('Erro ao buscar horários:', error);
        throw error;
    }
};

export const useHorarios = (params, options = {}) => {
    const stableParams = useMemo(() => ({
        tipoEntidade: params?.tipoEntidade || null,
        entidadeAgendavel: params?.entidadeAgendavel || null,
        unidade: params?.unidade || null
    }), [params?.tipoEntidade, params?.entidadeAgendavel, params?.unidade]);

    const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
        queryKey: [HORARIOS_QUERY_KEY, stableParams],
        queryFn: () => fetchHorarios(stableParams),
        enabled: !!stableParams.tipoEntidade && !!stableParams.entidadeAgendavel && options.enabled !== false,
        staleTime: 2 * 60 * 1000 // 2 minutos
    });

    return { data, isLoading, isError, error, refetch, isFetching };
};

/**
 * ✅ CORREÇÃO CRÍTICA: Normaliza data UTC para data local ignorando timezone
 * 
 * Exemplo: "2026-05-09T00:00:00.000Z" (UTC)
 * Normaliza para: 09/05/2026 no horário local
 */
const normalizeToLocalDate = (dateString) => {
    if (!dateString) return null;
    
    try {
        // Se for string ISO, extrai apenas ano, mês, dia
        if (typeof dateString === 'string') {
            // Tenta extrair YYYY-MM-DD da string
            const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);
            if (match) {
                const [, year, month, day] = match;
                // Cria data no horário local (meio-dia para evitar deslocamentos)
                return new Date(parseInt(year), parseInt(month) - 1, parseInt(day), 12, 0, 0);
            }
        }
        
        // Se for objeto Date, normaliza
        if (dateString instanceof Date && !isNaN(dateString.getTime())) {
            return new Date(
                dateString.getUTCFullYear(),
                dateString.getUTCMonth(),
                dateString.getUTCDate(),
                12, 0, 0
            );
        }
        
        // Fallback: tenta parseISO e depois normaliza
        const parsed = parseISO(dateString);
        if (!isNaN(parsed.getTime())) {
            return new Date(
                parsed.getUTCFullYear(),
                parsed.getUTCMonth(),
                parsed.getUTCDate(),
                12, 0, 0
            );
        }
    } catch (e) {
        console.error('Erro ao normalizar data:', e);
    }
    
    return null;
};

/**
 * ✅ CORREÇÃO CRÍTICA: Compara duas datas ignorando timezone
 */
const isSameLocalDate = (date1, date2) => {
    if (!date1 || !date2) return false;
    
    const d1 = date1 instanceof Date ? date1 : normalizeToLocalDate(date1);
    const d2 = date2 instanceof Date ? date2 : normalizeToLocalDate(date2);
    
    if (!d1 || !d2) return false;
    
    return d1.getFullYear() === d2.getFullYear() &&
           d1.getMonth() === d2.getMonth() &&
           d1.getDate() === d2.getDate();
};

/**
 * ✅ CORREÇÃO CRÍTICA: Busca dias de trabalho considerando DATA_UNICA
 * Agora normaliza as datas para evitar deslocamento de fuso
 */
const fetchDiasDeTrabalho = async (params) => {
    try {
        const { data } = await apiClient.get('/horarios/dias-de-trabalho', { params });
        if (!data.data || !Array.isArray(data.data)) return { diasSemana: [], datasUnicas: [] };
        
        // Separa os horários por tipo
        const diasSemana = [];
        const datasUnicas = [];
        
        data.data.forEach(horario => {
            if (horario.tipo === 'SEMANAL' && horario.diaDaSemana !== undefined) {
                diasSemana.push(horario.diaDaSemana);
            } 
            else if (horario.tipo === 'DATA_UNICA' && horario.data) {
                // ✅ CORREÇÃO: Normaliza a data única para horário local
                const dataNormalizada = normalizeToLocalDate(horario.data);
                if (dataNormalizada) {
                    datasUnicas.push(dataNormalizada);
                    console.log('📅 [fetchDiasDeTrabalho] Data única normalizada:', 
                        format(dataNormalizada, 'dd/MM/yyyy'), 
                        'Original:', horario.data);
                }
            }
        });
        
        const result = {
            diasSemana: [...new Set(diasSemana)],
            datasUnicas: datasUnicas
        };
        
        console.log('📅 [fetchDiasDeTrabalho] Resultado final:', {
            temDiasSemana: result.diasSemana.length > 0,
            diasSemana: result.diasSemana.map(d => {
                const diasMap = {0:'Dom',1:'Seg',2:'Ter',3:'Qua',4:'Qui',5:'Sex',6:'Sáb'};
                return diasMap[d];
            }),
            temDatasUnicas: result.datasUnicas.length > 0,
            datasUnicas: result.datasUnicas.map(d => format(d, 'dd/MM/yyyy'))
        });
        
        return result;
    } catch (error) {
        console.error('Erro ao buscar dias de trabalho unificados:', error);
        return { diasSemana: [], datasUnicas: [] };
    }
};

/**
 * Hook para buscar dias de trabalho (semanais e datas únicas)
 * ✅ Retorna objeto com diasSemana e datasUnicas (já normalizadas)
 */
export const useDiasDeTrabalho = (entidadeId, tipoEntidade = 'User') => {
    const params = useMemo(() => ({
        entidadeAgendavel: entidadeId,
        tipoEntidade: tipoEntidade
    }), [entidadeId, tipoEntidade]);
    
    return useQuery({
        queryKey: [HORARIOS_QUERY_KEY, 'diasDeTrabalho', entidadeId, tipoEntidade],
        queryFn: () => fetchDiasDeTrabalho(params),
        enabled: !!entidadeId,
        staleTime: 5 * 60 * 1000 // 5 minutos de cache
    });
};

/**
 * ✅ NOVO HOOK: Verifica se uma data específica é um dia de trabalho
 * Considera tanto dias da semana recorrentes quanto datas únicas
 * ✅ CORREÇÃO: Usa comparação local ignorando timezone
 */
export const useIsDiaDeTrabalho = (entidadeId, data, tipoEntidade = 'User') => {
    const { data: diasDeTrabalhoData, isLoading } = useDiasDeTrabalho(entidadeId, tipoEntidade);
    
    const isDiaDeTrabalho = useMemo(() => {
        if (!data || !diasDeTrabalhoData) return false;
        
        // Normaliza a data de entrada
        const dataNormalizada = normalizeToLocalDate(data);
        if (!dataNormalizada) return false;
        
        const diaSemana = dataNormalizada.getDay(); // 0 = domingo, 1 = segunda...
        
        // Verifica se é uma data única (usando comparação normalizada)
        const isDataUnica = diasDeTrabalhoData.datasUnicas?.some(dataUnica => 
            isSameLocalDate(dataUnica, dataNormalizada)
        );
        
        if (isDataUnica) {
            console.log(`✅ [isDiaDeTrabalho] ${format(dataNormalizada, 'dd/MM/yyyy')} é DATA ÚNICA`);
            return true;
        }
        
        // Verifica se o dia da semana está nos dias de trabalho semanais
        const isDiaSemanaTrabalho = diasDeTrabalhoData.diasSemana?.includes(diaSemana);
        
        // Se tem dias semanais configurados, verifica se o dia está incluído
        if (diasDeTrabalhoData.diasSemana?.length > 0) {
            return isDiaSemanaTrabalho;
        }
        
        // Se NÃO tem dias semanais configurados, só retorna true se for data única
        return false;
    }, [data, diasDeTrabalhoData]);
    
    return { isDiaDeTrabalho, isLoading };
};

export const useMutateHorarios = () => {
    const queryClient = useQueryClient();
    const { setAlert } = useAlert();

    const invalidateHorariosCache = () => {
        queryClient.invalidateQueries({ queryKey: [HORARIOS_QUERY_KEY] });
        queryClient.invalidateQueries({ queryKey: ['disponibilidade'] });
        queryClient.invalidateQueries({ queryKey: ['horariosTrabalho'] });
    };

    const createHorario = useMutation({
        mutationFn: async (novoHorario) => {
            const { data } = await apiClient.post('/horarios', novoHorario);
            return data;
        },
        onSuccess: () => {
            invalidateHorariosCache();
            setAlert('Regra de horário criada com sucesso!', 'success');
        },
        onError: (err) => {
            const errorMsg = err.response?.data?.error || err.response?.data?.message || 'Erro ao criar regra de horário';
            setAlert(errorMsg, 'danger');
        }
    });

    const updateHorario = useMutation({
        mutationFn: async ({ id, horarioData }) => {
            const { data } = await apiClient.put(`/horarios/${id}`, horarioData);
            return data;
        },
        onSuccess: () => {
            invalidateHorariosCache();
            setAlert('Regra de horário atualizada com sucesso!', 'success');
        },
        onError: (err) => {
            const errorMsg = err.response?.data?.error || err.response?.data?.message || 'Erro ao atualizar regra de horário';
            setAlert(errorMsg, 'danger');
        }
    });

    const deleteHorario = useMutation({
        mutationFn: async (id) => {
            const { data } = await apiClient.delete(`/horarios/${id}`);
            return data;
        },
        onSuccess: () => {
            invalidateHorariosCache();
            setAlert('Regra de horário removida.', 'success');
        },
        onError: (err) => {
            const errorMsg = err.response?.data?.error || err.response?.data?.message || 'Erro ao deletar regra de horário';
            setAlert(errorMsg, 'danger');
        }
    });

    return { 
        createHorario, 
        updateHorario, 
        deleteHorario 
    };
};

// Exportação padrão para compatibilidade
export default useHorarios;